import { tKey } from "./i18n.ts";

const API = "";

/**
 * Reload-on-401 is ARMED only once the boot sequence has seen a signed-in session
 * (app.ts calls this). Unarmed, a 401 is just an error: while signed OUT on a saas
 * server, boot-time background calls (theme, curator status) 401 before /api/meta even
 * resolves - reloading on those spins the login screen in an endless refresh loop.
 * Armed, a mid-use 401 means the session expired, and landing the whole app back on the
 * sign-in screen beats erroring every widget on the open page.
 *
 * ⚠ It NAVIGATES to /signin rather than reloading, which round 137 made load-bearing: `/` is the
 * landing page for a visitor with no session now, so a plain reload would answer an expired session
 * with marketing copy instead of the sign-in form. `replace` rather than `assign` - the dead page
 * they were on is not somewhere Back should return to.
 */
export const SIGNIN_PATH = "/signin";
let sessionReloadArmed = false;
export function armSessionReload(): void {
	sessionReloadArmed = true;
}

export async function api<T = any>(path: string, opts: { method?: string; headers?: Record<string, string>; body?: any } = {}): Promise<T> {
	const { headers, body, ...rest } = opts;
	const resp = await fetch(`${API}${path}`, {
		...rest,
		headers: { "Content-Type": "application/json", ...headers },
		body: body ? JSON.stringify(body) : undefined
	});
	if (!resp.ok) {
		const err = await resp.json().catch(() => ({ error: resp.statusText }));
		// "Signed out" is the saas session gate's marker (middleware/auth.ts) - see
		// armSessionReload above for why this only fires once a session was seen.
		if (sessionReloadArmed && resp.status === 401 && err.error === "Signed out") {
			location.replace(SIGNIN_PATH);
			return new Promise<never>(() => {});
		}
		// A coded error (src/errors.ts) carries a stable machine key the server picked - say it in
		// the reader's locale. t() returns the key itself when there is no translation, which is the
		// signal to fall back to the server's English `error` (an un-migrated route). ⚠ That fallback
		// is a SILENT one: a response whose `code` is not a dictionary key looks identical here to
		// one that has no code at all, so the reader gets English and nothing reports it. The AI
		// budget 402 sat in exactly that gap until round 158 - its `code` was the machine tag
		// `ai_budget_exhausted`, which resolves to itself. A 402 body's machine tag now rides in
		// `reason` and `code` is the `ai.wall.*` key, which this resolves like any other.
		//
		// ⚠ Through `tKey`, not `t`, since round 151. A coded error IS the round-70 seam's payload -
		// `apiRefusal` literally builds one from a `KeyText` - so it inherits the reader-side rules a
		// bare `t()` does not apply: a `{date}` param arrives ISO, and a number needs the locale's own
		// arithmetic ("1,5", not "1.5"). CLAUDE.md already called `tKey` the ONE resolver for this
		// seam; this is the call site that made that true.
		const coded = typeof err.code === "string" ? tKey({ key: err.code, params: err.errorParams }) : null;
		const msg = coded && coded !== err.code ? coded : err.details?.length ? err.details.join(", ") : err.error || resp.statusText;
		throw new Error(msg);
	}
	return resp.json();
}
